import React from 'react';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import {
  VseClassComponentEditTable,
  ComponentErrorKeyResolver,
  ComponentScheduleErrorKeyResolver,
} from './VseClassComponentEditTable';
import { VseClassEditTemplate, VseClassComponentEditTemplate, VseClassComponentScheduleEditTemplate } from '../types/VseClassEditTemplate';
import { VseTextField } from '../utils/VseTextField';
import {
  TeachingModeEntity,
  TimeZoneEntity,
} from '../types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  hidden: {
    display: 'none',
  },
}));

export type ClassErrorKeyResolver = (key: keyof VseClassEditTemplate) => string

export interface VseClassEditGridProps {
  data: VseClassEditTemplate,
  readOnly?: boolean,
  teachingModeOptions: TeachingModeEntity[],
  timeZoneOptions: TimeZoneEntity[],
  onChange: (changes: Partial<VseClassEditTemplate>) => void,
  onAdd: () => void,
  onDelete: () => void,
  onComponentChange: (compIndex: number, changes: Partial<VseClassComponentEditTemplate>) => void
  onComponentAdd: () => void,
  onComponentDelete: (compIndex: number) => void,
  onScheduleChange: (compIndex: number, schdIndex: number, changes: Partial<VseClassComponentScheduleEditTemplate>) => void,
  onScheduleAdd: (compIndex: number) => void,
  onScheduleDelete: (compIndex: number, schdIndex: number) => void,
  last: boolean,
  resolveClassErrorKey: ClassErrorKeyResolver,
  resolveComponentErrorKey: ComponentErrorKeyResolver,
  resolveComponentScheduleErrorKey: ComponentScheduleErrorKeyResolver,
  valProblemKeys?: Array<string>,
}

export function VseClassEditGrid(props: VseClassEditGridProps) {
  const theme = useTheme();
  const classes = useStyles(theme);

  function hasError(key: keyof VseClassEditTemplate): boolean {
    if (props.valProblemKeys) {
      const searchElement: string = props.resolveClassErrorKey(key);
      return props.valProblemKeys.includes(searchElement);
    }
    return false;
  }
  return (
    <div className={classes.root}>
      <Grid container>
        <Grid container>
          <Grid item xs={12} md={9}>
            <VseTextField
              label='Class Code'
              required
              readOnly={props.readOnly}
              value={props.data.className}
              onChange={(e) => props.onChange({ className: e.target.value })}
              error={hasError('className')}
            />
          </Grid>
          {!props.readOnly && (
            <Grid item xs={12} md={3}>
              <Grid container justifyContent='flex-end'>
                <IconButton
                  className={clsx(!props.last && classes.hidden, props.readOnly && classes.hidden)}
                  aria-label='Add Class'
                  color='secondary'
                  onClick={() => props.onAdd()}
                >
                  <AddIcon />
                </IconButton>
                <IconButton
                  className={clsx(props.readOnly && classes.hidden)}
                  aria-label='Delete Class'
                  onClick={() => props.onDelete()}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          )}
        </Grid>
        <VseClassComponentEditTable
          list={props.data.components}
          readOnly={props.readOnly}
          teachingModeOptions={props.teachingModeOptions}
          timeZoneOptions={props.timeZoneOptions}
          onChange={props.onComponentChange}
          onAdd={props.onComponentAdd}
          onDelete={props.onComponentDelete}
          onScheduleChange={props.onScheduleChange}
          onScheduleAdd={props.onScheduleAdd}
          onScheduleDelete={props.onScheduleDelete}
          resolveComponentErrorKey={props.resolveComponentErrorKey}
          resolveComponentScheduleErrorKey={props.resolveComponentScheduleErrorKey}
          valProblemKeys={props.valProblemKeys}
        />
      </Grid>
    </div>
  );
}
